import type { GroupPublic } from '@repo/types/groups';

import { useDeleteGroup } from '@repo/api/group/useDeleteGroup';
import { baseErrorMessages } from '@repo/lib/messages/baseErrorMessages';
import { BookTextIcon } from 'lucide-react';
import { useEffect, useMemo, type ReactNode } from 'react';

import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/dialog';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';

/**
 * Props for the DeleteGroup component.
 * @property {GroupPublic[] | null} [groups] - Array of groups to delete.
 * @property {boolean} open - Whether the dialog is open.
 * @property {(value: boolean) => void} onOpenChange - Callback to change dialog open state.
 */
interface DeleteGroupProps {
  groups?: GroupPublic[] | null;
  open: boolean;
  onOpenChange: (value: boolean) => void;
}

export default function DeleteGroup(deleteGroupProps: DeleteGroupProps) {
  const { groups, open, onOpenChange } = deleteGroupProps;
  const isSingle: boolean = groups?.length === 1;

  const { mutateAsync, reset } = useDeleteGroup();

  useEffect(() => {
    if (open) {
      reset();
    }
  }, [open, reset]);

  const onSubmit = async () => {
    if (!groups) return;
    await Promise.all(groups.map((element) => mutateAsync(element.id))).catch((error) => {
      toasterMessageHandler(baseErrorMessages, error);
    });
    onClose();
  };

  const onClose = () => {
    onOpenChange(false);
  };

  const header = useMemo<string>(() => {
    if (isSingle) {
      return 'Usunąć Grupę ?';
    } else {
      return `Usunąć ${groups?.length} grupy?`;
    }
  }, [isSingle, groups]);

  const content = useMemo<ReactNode>(() => {
    if (isSingle) {
      return (
        <p className={'text-text-secondary'}>
          Czy na pewno chcesz usunąć grupę{'  '}
          <b>{groups?.[0]?.name}</b>?
        </p>
      );
    } else {
      return <p className={'text-text-secondary'}>Czy na pewno chcesz usunąć wybrane grupy ?</p>;
    }
  }, [isSingle, groups]);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className={'sm:max-w-96'}>
        <AlertDialogBody>
          <AlertDialogHeader>
            <FeaturedIcon size={'lg'}>
              <BookTextIcon />
            </FeaturedIcon>
            <div className={'flex min-w-0 grow flex-col gap-0.5'}>
              <AlertDialogTitle>{header}</AlertDialogTitle>
              <AlertDialogDescription className={'text-muted-foreground text-xs'}>Usuń wybrane grupy/ę.</AlertDialogDescription>
            </div>
          </AlertDialogHeader>
          <div className={'flex flex-col gap-3 overflow-auto px-4'}>
            {content}
            {!isSingle && groups && (
              <div className={'flex flex-col gap-3'}>
                <h5 className={'text-muted-foreground text-sm font-medium'}>Grupy:</h5>
                <div className={'flex flex-wrap gap-2'}>
                  {groups.map(({ id, name }) => {
                    return (
                      <Badge key={id} size={'sm'} variant={'muted'}>
                        {name}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </AlertDialogBody>
        <AlertDialogFooter className={'border-border-primary dark:bg-card bg-accent border-t'}>
          <AlertDialogCancel variant={'tertiary'} onClick={onClose}>
            Zamknij
          </AlertDialogCancel>
          <AlertDialogAction onClick={onSubmit}>Potwierdź</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
